import PropTypes from 'prop-types';
import React from 'react';
import { FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { makeStyles } from "@material-ui/core";
import classNames from 'classnames';
import AppColor from "utilities/AppColor";
import { getIn } from "formik";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
  geocodeByPlaceId
} from 'react-places-autocomplete';
import AppConfig from "config/AppConfig";

const useStyles = makeStyles(() => ({
  input: {
    border: '1px solid rgba(0, 0, 0, 0.2)',
    boxSizing: 'border-box',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: 5,
    height: '45px',
    '&:focus': {
      border: `1px solid ${AppColor.mainColor}`,
      boxSizing: 'border-box',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: 5,
    }
  },
  label: {
    fontSize: 14,
    color: AppColor.black,
    fontWeight: '500'
  }
}))

const InputAutocompleteAddress = (props) => {
  const {
    field, form, onChangePostalCode, onChangeLatLng,
    type, label, placeholder, disabled, id, errorMessage,
    onChange, required, className, inputClass, isTouched,
    setLatLng, allowPattern, onAddressChange, setValidAddress
  } = props;
  const classes = useStyles();
  const { name, value = '' } = field;
  const { errors, touched, setFieldTouched } = form;
  const error = getIn(errors, name);
  const touch = getIn(touched, name);

  let showError = error && touch;
  const { t } = useTranslation();

  if (errorMessage) {
    showError = !!errorMessage && isTouched;
  }

  // Filter function to remove disallowed characters
  const filterAllowedChars = (input) => {
    if (!allowPattern) {
      return input;
    }
    // Use pattern from AppConfig to filter invalid characters
    const pattern = AppConfig.PATTERNS.ADDRESS_PATTERN;
    // Remove characters that don't match the pattern
    return input.split('').filter(char => pattern.test(char)).join('');
  };

  const handleChangeInput = (value) => {
    const changeEvent = {
      target: {
        name: name,
        value: value
      }
    };
    field.onChange(changeEvent);

    setFieldTouched && setFieldTouched(name, true, false);
    onChange && onChange(value);
  };

  const handleChange = address => {
    if (setLatLng) {
      setLatLng({});
    }
    // Filter the address to only allow valid characters when allowPattern is true
    const filteredAddress = allowPattern ? filterAllowedChars(address) : address;
    // Notify parent that user has modified without selecting a suggestion
    onAddressChange && onAddressChange(filteredAddress);
    handleChangeInput(filteredAddress);
  };

  const handleSelect = async (address, placeId) => {
    // Filter the selected address to only allow valid characters when allowPattern is true
    const filteredAddress = allowPattern ? filterAllowedChars(address) : address;
    // Notify parent that user selected a suggestion
    setValidAddress && setValidAddress(true);
    handleChangeInput(filteredAddress);
    geocodeByAddress(filteredAddress)
      .then(results => getLatLng(results[0]))
      .then(latLng => onChangeLatLng(latLng))
      .catch(error => {
        // setLatLng && setLatLng({});
        setValidAddress && setValidAddress(false);
        console.error('Error', error)
      });

    const [place] = await geocodeByPlaceId(placeId);
    const { long_name: postalCode = '' } =
      place.address_components.find(c => c.types.includes('postal_code')) || {};
    postalCode && postalCode !== '' && onChangePostalCode && onChangePostalCode(postalCode);
  };

  return (
    <FormGroup className={className}>
      <div className="d-flex">
        <div className="w-100">
          <>
            {label && <Label className={classes.label} for={id || name}>{t(label)}{required && (<span style={{ color: AppColor.red }}>*</span>)}</Label>}
            <PlacesAutocomplete
              value={value}
              onChange={handleChange}
              onSelect={handleSelect}
              debounce={600}
              shouldFetchSuggestions={value.trim().length > 3}
              searchOptions ={{
                componentRestrictions: {country: 'au'},
                types: ['address']
              }}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div>
                  <Input
                    id={id || name}
                    {...field}
                    className={classNames([classes.input, inputClass])}
                    type={type}
                    disabled={disabled}
                    placeholder={t(placeholder)}
                    onChange={(event) => {
                      const filteredValue = filterAllowedChars(event.target.value);
                      // Notify parent that user has modified without selecting a suggestion
                      onAddressChange && onAddressChange(filteredValue);
                      handleChangeInput(filteredValue);
                    }}
                    onKeyPress={event => {
                      if (type === 'number' && event.key === 'e') {
                        event.preventDefault();
                      } else if (allowPattern) {
                        // Only validate if allowPattern is true
                        const char = event.key;
                        const pattern = AppConfig.PATTERNS.ADDRESS_PATTERN;
                        if (!pattern.test(char)) {
                          event.preventDefault();
                        }
                      }
                    }}
                    invalid={showError}
                    {...getInputProps({
                      placeholder: placeholder ? t(placeholder) : `${t('text.searchPlace')}...`,
                      className: `location-search-input ${classes.input} ${inputClass}`,
                      disabled: disabled
                    })}
                  />
                  <div className="autocomplete-dropdown-container">
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion, index) => {
                      const className = suggestion.active
                        ? 'suggestion-item--active'
                        : 'suggestion-item';
                      // inline style for demonstration purpose
                      const style = suggestion.active
                        ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                        : { backgroundColor: '#ffffff', cursor: 'pointer' };
                      return (
                        <div
                          {...getSuggestionItemProps(suggestion, {
                            className,
                            style,
                          })}
                          key={`key-${index}`}
                        >
                          <strong>
                            {suggestion.formattedSuggestion.mainText}
                          </strong>{' '}
                          <small>
                            {suggestion.formattedSuggestion.secondaryText}
                          </small>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </PlacesAutocomplete>
          </>
        </div>
      </div>
      {showError && (
        <FormFeedback style={{
          display: showError ? 'inline' : 'none'
        }}>
          {typeof error === "object"
            ? t(error.message, { value: error.value })
            : t(error)
          }
        </FormFeedback>
      )}
    </FormGroup>
  );
}

InputAutocompleteAddress.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,

  type: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.any,
  errorMessage: PropTypes.any,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  isTouched:PropTypes.bool,
  className: PropTypes.any,
  inputClass: PropTypes.any,
  onChangePostalCode: PropTypes.func,
  onChangeLatLng: PropTypes.func,
  setLatLng: PropTypes.func,
  allowPattern: PropTypes.bool,
  onAddressChange: PropTypes.func,
  setValidAddress: PropTypes.func,
};

InputAutocompleteAddress.defaultProps = {
  type: 'text',
  label: '',
  placeholder: '',
  disabled: false,
  id: '',
  allowPattern: false
};

export default InputAutocompleteAddress;
